/**
 * Analytics utility for tracking user behavior and conversion events
 * Integrates with Google Analytics, Mixpanel, or custom analytics
 */

import logger from '../utils/logger';
import { hasConsentFor } from '../hooks/useConsent';
import {
  getPrivacySafePath,
  isSensitiveCredentialRoute,
} from './sensitiveRoutePrivacy';

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
  metadata?: Record<string, any>;
}

interface UserProperties {
  userId?: string;
  email?: string;
  plan?: string;
  level?: number;
  credits?: number;
  signupDate?: string;
}

const analyticsAllowed = (): boolean => (
  hasConsentFor('analytics') && !isSensitiveCredentialRoute()
);

const safeUserProperties = (properties?: UserProperties): Omit<UserProperties, 'email'> | undefined => {
  if (!properties) return undefined;
  const { email: _email, ...allowed } = properties;
  return allowed;
};

class Analytics {
  private initialized = false;
  private debugMode = false;

  constructor() {
    this.debugMode = process.env.NODE_ENV === 'development';
  }

  /**
   * Initialize analytics providers
   */
  init() {
    if (this.initialized || !analyticsAllowed()) return;

    try {
      const gaTrackingId = process.env.REACT_APP_GA_TRACKING_ID?.trim();
      const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN?.trim();
      // Initialize Google Analytics
      if (window.gtag && gaTrackingId) {
        window.gtag('js', new Date());
        window.gtag('config', gaTrackingId);
      }

      // Initialize Mixpanel (if available)
      if (window.mixpanel && mixpanelToken) {
        window.mixpanel.init(mixpanelToken);
      }

      this.initialized = true;
      this.log('Analytics initialized');
    } catch (error) {
      logger.error('Failed to initialize analytics:', error);
    }
  }

  /**
   * Track a page view
   */
  pageView(path: string, title?: string) {
    if (!analyticsAllowed()) return;

    try {
      const safePath = getPrivacySafePath(path);
      // Google Analytics
      if (window.gtag) {
        window.gtag('config', process.env.REACT_APP_GA_TRACKING_ID || '', {
          page_path: safePath,
          page_title: title || document.title
        });
      }

      // Mixpanel
      if (window.mixpanel) {
        window.mixpanel.track('Page View', {
          path: safePath,
          title: title || document.title
        });
      }

      this.log('Page view tracked:', safePath);
    } catch (error) {
      logger.error('Failed to track page view:', error);
    }
  }

  /**
   * Track a custom event
   */
  track(event: AnalyticsEvent) {
    if (!analyticsAllowed()) return;

    try {
      const { category, action, label, value, metadata } = event;

      // Google Analytics
      if (window.gtag) {
        window.gtag('event', action, {
          event_category: category,
          event_label: label,
          value,
          ...metadata
        });
      }

      // Mixpanel
      if (window.mixpanel) {
        window.mixpanel.track(`${category} - ${action}`, {
          label,
          value,
          ...metadata
        });
      }

      this.log('Event tracked:', event);
    } catch (error) {
      logger.error('Failed to track event:', error);
    }
  }

  /**
   * Identify a user
   */
  identify(userId: string, properties?: UserProperties) {
    if (!analyticsAllowed()) return;

    try {
      const allowedProperties = safeUserProperties(properties);
      // Google Analytics
      if (window.gtag) {
        window.gtag('set', { user_id: userId });
        if (allowedProperties) {
          window.gtag('set', 'user_properties', allowedProperties);
        }
      }

      // Mixpanel
      if (window.mixpanel) {
        window.mixpanel.identify(userId);
        if (allowedProperties) {
          window.mixpanel.people.set(allowedProperties);
        }
      }

      this.log('User identified:', userId, allowedProperties);
    } catch (error) {
      logger.error('Failed to identify user:', error);
    }
  }

  /**
   * Track conversion event
   */
  conversion(eventName: string, value?: number, metadata?: Record<string, any>) {
    if (!analyticsAllowed()) return;

    try {
      // Google Analytics conversion
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: `${process.env.REACT_APP_GA_TRACKING_ID}/${eventName}`,
          value,
          currency: 'USD',
          ...metadata
        });
      }

      // Mixpanel
      if (window.mixpanel) {
        window.mixpanel.track('Conversion', {
          event_name: eventName,
          value,
          ...metadata
        });
      }

      this.log('Conversion tracked:', eventName, value);
    } catch (error) {
      logger.error('Failed to track conversion:', error);
    }
  }

  /**
   * Track tool usage
   */
  toolUsed(toolName: string, metadata?: Record<string, any>) {
    this.track({
      category: 'Tool Usage',
      action: 'Tool Completed',
      label: toolName,
      metadata: {
        tool: toolName,
        timestamp: new Date().toISOString(),
        ...metadata
      }
    });
  }

  /**
   * Track purchase
   */
  purchase(transactionId: string, value: number, items: any[]) {
    if (!analyticsAllowed()) return;

    try {
      // Google Analytics ecommerce
      if (window.gtag) {
        window.gtag('event', 'purchase', {
          transaction_id: transactionId,
          value,
          currency: 'USD',
          items
        });
      }

      // Mixpanel
      if (window.mixpanel) {
        window.mixpanel.track('Purchase', {
          transaction_id: transactionId,
          value,
          items
        });
        window.mixpanel.people.track_charge(value);
      }

      this.log('Purchase tracked:', transactionId, value);
    } catch (error) {
      logger.error('Failed to track purchase:', error);
    }
  }

  /**
   * Track signup
   */
  signup(method: string, userId?: string) {
    this.track({
      category: 'Authentication',
      action: 'Sign Up',
      label: method,
      metadata: { method, userId }
    });

    this.conversion('signup', 0, { method });
  }

  /**
   * Track login
   */
  login(method: string, userId?: string) {
    this.track({
      category: 'Authentication',
      action: 'Login',
      label: method,
      metadata: { method, userId }
    });
  }

  /**
   * Track feature usage
   */
  featureUsed(featureName: string, metadata?: Record<string, any>) {
    this.track({
      category: 'Feature',
      action: 'Used',
      label: featureName,
      metadata
    });
  }

  /**
   * Track error
   */
  error(errorName: string, _errorMessage: string, metadata?: Record<string, any>) {
    this.track({
      category: 'Error',
      action: errorName,
      label: 'FrontendError',
      metadata
    });
  }

  private log(...args: any[]) {
    if (this.debugMode) {
      logger.info('[Analytics]', ...args);
    }
  }
}

// Extend Window interface for analytics providers
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    mixpanel?: any;
    dataLayer?: any[];
  }
}

// Create singleton instance
export const analytics = new Analytics();

// Convenience hooks
export const useAnalytics = () => {
  return {
    trackPageView: (path: string, title?: string) => analytics.pageView(path, title),
    trackEvent: (event: AnalyticsEvent) => analytics.track(event),
    trackToolUsage: (toolName: string, metadata?: Record<string, any>) => 
      analytics.toolUsed(toolName, metadata),
    trackPurchase: (transactionId: string, value: number, items: any[]) => 
      analytics.purchase(transactionId, value, items),
    trackSignup: (method: string, userId?: string) => analytics.signup(method, userId),
    trackLogin: (method: string, userId?: string) => analytics.login(method, userId),
    trackFeature: (featureName: string, metadata?: Record<string, any>) => 
      analytics.featureUsed(featureName, metadata),
    identifyUser: (userId: string, properties?: UserProperties) => 
      analytics.identify(userId, properties),
  };
};

export default analytics;
